/**
 * @module NotificationPreferencesForm
 * @package @geenius/notifications-solidjs
 * @description Renders the shared preference controls for the Tailwind SolidJS
 * variant.
 */

import { motion } from '@geenius/motion/solidjs'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  getNotificationChannelLabel,
  getNotificationTypeLabel,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { For } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Async or sync handler used by the preferences form.
 */
export type NotificationPreferencesFormUpdate = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => void | Promise<void>

/**
 * Props for the SolidJS preferences form.
 */
export interface NotificationPreferencesFormProps {
  preferences: NotificationPreferences
  onUpdate: NotificationPreferencesFormUpdate
  class?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']

/**
 * Renders the notification preferences form.
 *
 * @param props Preference data and update handler.
 * @returns The preference form markup.
 */
export function NotificationPreferencesForm(
  props: NotificationPreferencesFormProps,
): JSX.Element {
  const { t } = createNotificationCopy()
  const quietHours = () => props.preferences.quietHours ?? { start: '22:00', end: '07:00' }

  return (
    <form
      class={cn('flex flex-col gap-6', props.class)}
      aria-label={t('notifications.preferences.formAria')}
    >
      <fieldset>
        <legend class="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.channels')}
        </legend>
        <div class="flex flex-col gap-2">
          <For each={CHANNELS}>
            {(channel) => {
              const inputId = `notifications-channel-${channel}`
              const channelLabel = getNotificationChannelLabel(channel, t)

              return (
                <motion.label
                  for={inputId}
                  whileHover={{ x: 2 }}
                  class="group flex cursor-pointer items-center justify-between rounded-xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-4 py-3 transition-colors hover:bg-[var(--gn-notifications-panel-surface)]"
                >
                  <span class="text-sm font-medium text-foreground transition-colors group-hover:text-white">
                    {channelLabel}
                  </span>
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={props.preferences.channels[channel]}
                    onChange={(event) =>
                      void props.onUpdate({
                        ...props.preferences.channels,
                        [channel]: event.currentTarget.checked,
                      })
                    }
                    aria-label={t('notifications.preferences.toggleChannelAria', {
                      channel: channelLabel,
                    })}
                    class="h-4 w-4 cursor-pointer accent-primary"
                  />
                </motion.label>
              )
            }}
          </For>
        </div>
      </fieldset>
      <fieldset>
        <legend class="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.types')}
        </legend>
        <div class="flex flex-col gap-2">
          <For each={NOTIFICATION_TYPES}>
            {(type) => {
              const inputId = `notifications-type-${type}`
              const typeLabel = getNotificationTypeLabel(type, t)

              return (
                <motion.label
                  for={inputId}
                  whileHover={{ x: 2 }}
                  class="group flex cursor-pointer items-center justify-between rounded-xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-4 py-3 transition-colors hover:bg-[var(--gn-notifications-panel-surface)]"
                >
                  <span class="flex items-center gap-2 text-sm font-medium text-foreground transition-colors group-hover:text-white">
                    {TYPE_CONFIG[type].icon}
                    <span>{typeLabel}</span>
                  </span>
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={props.preferences.types[type]}
                    onChange={(event) =>
                      void props.onUpdate(undefined, {
                        ...props.preferences.types,
                        [type]: event.currentTarget.checked,
                      })
                    }
                    aria-label={t('notifications.preferences.toggleTypeAria', {
                      type: typeLabel,
                    })}
                    class="h-4 w-4 cursor-pointer accent-primary"
                  />
                </motion.label>
              )
            }}
          </For>
        </div>
      </fieldset>
      <fieldset>
        <legend class="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.quietHours')}
        </legend>
        <div class="flex items-center gap-3">
          <label
            class="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            for="notifications-quiet-start"
          >
            {t('notifications.preferences.fields.start')}
            <input
              id="notifications-quiet-start"
              type="time"
              value={quietHours().start}
              onInput={(event) =>
                void props.onUpdate(undefined, undefined, {
                  start: event.currentTarget.value,
                  end: quietHours().end,
                })
              }
              class="rounded-lg border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-backdrop)] px-3 py-2 text-sm text-foreground transition-colors focus:border-primary/50 focus:outline-none"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            for="notifications-quiet-end"
          >
            {t('notifications.preferences.fields.end')}
            <input
              id="notifications-quiet-end"
              type="time"
              value={quietHours().end}
              onInput={(event) =>
                void props.onUpdate(undefined, undefined, {
                  start: quietHours().start,
                  end: event.currentTarget.value,
                })
              }
              class="rounded-lg border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-backdrop)] px-3 py-2 text-sm text-foreground transition-colors focus:border-primary/50 focus:outline-none"
            />
          </label>
        </div>
      </fieldset>
    </form>
  )
}
